// src/components/ArabicTextField.js
//
// Drop-in replacement for MUI <TextField> for Arabic-language fields.
// Adds a compact "AR" button in the end adornment that opens a small
// Arabic keyboard popover. The keyboard inserts characters at the
// current caret position and closes automatically on blur or outside
// click (Popover handles the latter, the blur handler the former).

import React, { useCallback, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import ArabicKeyboard from './ArabicKeyboard';

const ArabicTextField = ({
  value,
  onChange,
  InputProps: userInputProps,
  inputRef: externalInputRef,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const selectionRef = useRef({ start: null, end: null });

  // Cache selection whenever the native input reports it. We lose the
  // real selection once focus moves to the keyboard button, so we must
  // restore it ourselves after every insert.
  const rememberSelection = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    if (typeof el.selectionStart === 'number') {
      selectionRef.current = {
        start: el.selectionStart,
        end: el.selectionEnd,
      };
    }
  }, []);

  const setRef = useCallback(
    (el) => {
      inputRef.current = el;
      if (typeof externalInputRef === 'function') externalInputRef(el);
      else if (externalInputRef) externalInputRef.current = el;
    },
    [externalInputRef],
  );

  const emitChange = (next) => {
    if (!onChange) return;
    // Fake enough of a change event for existing consumers that read
    // e.target.value. Real DOM event isn't needed because we already
    // know the new string.
    onChange({ target: { value: next } });
  };

  const updateAtCaret = (mutator) => {
    const current = value ?? '';
    const { start, end } = selectionRef.current;
    let caret = typeof start === 'number' ? start : current.length;
    let caretEnd = typeof end === 'number' ? end : caret;
    const result = mutator(current, caret, caretEnd);
    if (!result) return;
    emitChange(result.value);
    const nextCaret = result.caret;
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      try {
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
      } catch (_) {}
      selectionRef.current = { start: nextCaret, end: nextCaret };
    });
  };

  const handleInsert = (ch) => {
    updateAtCaret((cur, a, b) => ({
      value: cur.slice(0, a) + ch + cur.slice(b),
      caret: a + ch.length,
    }));
  };

  const handleBackspace = () => {
    updateAtCaret((cur, a, b) => {
      if (a !== b) {
        return { value: cur.slice(0, a) + cur.slice(b), caret: a };
      }
      if (a === 0) return null;
      return { value: cur.slice(0, a - 1) + cur.slice(a), caret: a - 1 };
    });
  };

  const toggleKeyboard = (e) => {
    rememberSelection();
    setAnchorEl((prev) => (prev ? null : e.currentTarget));
  };

  const handleBlur = (e) => {
    // Every keyboard button preventDefaults mousedown, so real blur
    // only fires when focus actually leaves the input (Tab, click on
    // another field, etc.) — close the keyboard in that case.
    setAnchorEl(null);
    if (rest.onBlur) rest.onBlur(e);
  };

  const mergedInputProps = {
    ...(userInputProps || {}),
    endAdornment: (
      <>
        {userInputProps?.endAdornment}
        <InputAdornment position="end">
          <Tooltip title="لوحة مفاتيح عربية">
            <IconButton
              size="small"
              onClick={toggleKeyboard}
              onMouseDown={(e) => {
                // Prevent the input losing focus before we read selection
                e.preventDefault();
                rememberSelection();
              }}
              aria-label="Open Arabic keyboard"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                width: 28,
                height: 28,
                border: (th) => `1px solid ${th.palette.divider}`,
                borderRadius: 1,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
              }}
            >
              AR
            </IconButton>
          </Tooltip>
        </InputAdornment>
      </>
    ),
  };

  return (
    <>
      <TextField
        {...rest}
        value={value ?? ''}
        onChange={(e) => {
          rememberSelection();
          if (onChange) onChange(e);
        }}
        onSelect={rememberSelection}
        onKeyUp={rememberSelection}
        onClick={rememberSelection}
        onBlur={handleBlur}
        inputRef={setRef}
        InputProps={mergedInputProps}
        sx={{
          maxWidth: 380,
          '& input, & textarea': { fontFamily: 'var(--sacred-font-body)' },
          ...rest.sx,
        }}
      />
      <ArabicKeyboard
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onInsert={handleInsert}
        onBackspace={handleBackspace}
      />
    </>
  );
};

export default ArabicTextField;
